import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, PageLoader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Calculator, Save } from "lucide-react";

const COST_FIELDS = [
  ["compute_per_vm", "Compute cost / VM"],
  ["storage_per_tb", "Storage cost / TB"],
  ["bandwidth", "Bandwidth / server"],
  ["payment_fee_pct", "Payment fee %"],
  ["payment_fee_flat", "Payment fee flat $"],
  ["support_alloc", "Support alloc / customer"],
];

export default function AdminEconomics() {
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState(null);

  const load = () => api.get("/admin/economics").then((r) => { setData(r.data); setCosts(r.data.costs); });
  useEffect(() => { load(); }, []);
  if (!data || !costs) return <PageLoader />;

  const save = async () => {
    try {
      await api.put("/admin/economics/costs", {
        compute_per_vm: Number(costs.compute_per_vm), storage_per_tb: Number(costs.storage_per_tb),
        bandwidth: Number(costs.bandwidth), payment_fee_pct: Number(costs.payment_fee_pct),
        payment_fee_flat: Number(costs.payment_fee_flat), support_alloc: Number(costs.support_alloc),
      });
      toast.success("Cost assumptions updated"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2"><Calculator className="h-5 w-5 text-cyan-400" /><h1 className="font-display text-2xl font-bold text-slate-50">Unit Economics</h1></div>
      <p className="mb-5 text-sm text-slate-400">Internal margin engine. Never hard-coded — all provider costs are configuration.</p>

      <GlassCard className="mb-6 p-6">
        <h2 className="mb-4 font-display font-semibold text-slate-100">Provider cost assumptions</h2>
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {COST_FIELDS.map(([k, l]) => (
            <div key={k}><label className="text-xs text-slate-500">{l}</label><Input type="number" step="0.01" value={costs[k]} onChange={(e) => setCosts({ ...costs, [k]: e.target.value })} data-testid={`cost-${k}`} className="mt-1 bg-slate-950 border-slate-700" /></div>
          ))}
        </div>
        <button onClick={save} data-testid="save-costs" className="mt-4 inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#06080D]"><Save className="h-4 w-4" /> Recalculate</button>
      </GlassCard>

      <GlassCard className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-3">Customer</th><th className="p-3">Revenue</th><th className="p-3">Compute</th><th className="p-3">Storage</th><th className="p-3">Bandwidth</th><th className="p-3">Payment</th><th className="p-3">Support</th><th className="p-3">Gross Profit</th><th className="p-3">Margin</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {data.rows.map((r, i) => (
              <tr key={i} className={`hover:bg-slate-900/40 ${r.below_target ? "bg-rose-500/5" : ""}`}>
                <td className="p-3 text-slate-200">{r.customer}</td>
                <td className="p-3 font-mono text-emerald-300">${r.revenue}</td>
                <td className="p-3 font-mono text-slate-400">${r.compute}</td>
                <td className="p-3 font-mono text-slate-400">${r.storage}</td>
                <td className="p-3 font-mono text-slate-400">${r.bandwidth}</td>
                <td className="p-3 font-mono text-slate-400">${r.payment_fee}</td>
                <td className="p-3 font-mono text-slate-400">${r.support}</td>
                <td className="p-3 font-mono text-slate-200">${r.gross_profit}</td>
                <td className={`p-3 font-mono font-semibold ${r.below_target ? "text-rose-400" : "text-emerald-400"}`}>{r.margin}%</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">No paying customers yet. Deploy a server (with checkout) to populate margins.</td></tr>}
          </tbody>
        </table>
      </GlassCard>
      <p className="mt-3 text-xs text-slate-500">Target infrastructure gross margin 60–75%. Rows below 50% are flagged. For internal business management only.</p>
    </div>
  );
}
